// src/pages/photobook/PhotobookCart.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Trash2, ChevronRight, ShoppingBag } from "lucide-react";
import { useCart } from "../../Context/Cartcontext";

function FontImport() {
  return (
    <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;1,9..144,500&family=DM+Sans:wght@400;500;600;700&display=swap');
    .og-display{font-family:'Fraunces',serif} .og-body{font-family:'DM Sans',sans-serif}`}</style>
  );
}

function SpreadThumb({ spread, bg }) {
  const TW = 120, TH = 90;
  return (
    <div className="flex gap-0.5">
      {spread.map((page, pi) => (
        <div key={pi} className="relative overflow-hidden border border-gray-200 rounded"
          style={{ width: TW, height: TH, backgroundColor: page.bg || bg || "#FFFFFF" }}>
          {page.layout?.slots.map((s, i) => (
            <div key={i} className="absolute overflow-hidden"
              style={{ left:`${s.x}%`, top:`${s.y}%`, width:`${s.w}%`, height:`${s.h}%` }}>
              {page.images?.[i]
                ? <img src={page.images[i]} alt="" className="w-full h-full object-cover"
                    style={{ filter:page.imageProps?.[i]?.filter||'none', transform:`rotate(${page.imageProps?.[i]?.rotation||0}deg) scale(${page.imageProps?.[i]?.zoom||1})` }}/>
                : <div className="w-full h-full bg-gray-100"/>}
            </div>
          ))}
          {!page.layout && (
            <div className="absolute" style={{ inset:"8%" }}>
              {page.images?.[0]
                ? <img src={page.images[0]} alt="" className="w-full h-full object-cover"/>
                : <div className="w-full h-full bg-gray-100"/>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function PhotobookCart() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();

  const [savedProjects, setSavedProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    if (location.state?.savedProject) setCurrentProject(location.state.savedProject);
    try {
      const stored = JSON.parse(localStorage.getItem("pb_saved_projects") || "[]");
      setSavedProjects(stored);
    } catch (e) {}
  }, []);

  const deleteProject = (id) => {
    const updated = savedProjects.filter(p => p.id !== id);
    setSavedProjects(updated);
    localStorage.setItem("pb_saved_projects", JSON.stringify(updated));
  };

  const addProjectToCart = (project) => {
    addToCart({
      id: project.id,
      name: `${project.productName} — ${project.projectName}`,
      subtitle: "Custom Photobook",
      price: 1499,
      comparePrice: 1699,
      image: project.spreads?.[0]?.[1]?.images?.[0] || "",
      bg: project.bg || "#FFFFFF",
      isPhotobook: true,
    }, 0);
    navigate("/checkout");
  };

  const allProjects = currentProject
    ? [currentProject, ...savedProjects.filter(p => p.id !== currentProject.id)]
    : savedProjects;

  const totalPages = Math.ceil(allProjects.length / itemsPerPage);
  const currentProjects = allProjects.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

  return (
    <div className="og-body min-h-screen bg-[#FAFAFA] text-[#1A1A1A]">
      <FontImport/>

      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 sm:px-6 py-3.5 sm:py-4">
        <div className="mx-auto max-w-5xl flex items-center justify-between gap-2 flex-wrap">
          <Link to="/" className="og-display text-xl sm:text-2xl italic font-semibold">Aishcraft</Link>
          <h1 className="text-sm sm:text-base font-bold order-3 sm:order-2 w-full sm:w-auto text-center sm:text-left">Your Photobooks</h1>
          <Link to="/photobook" className="text-[13px] sm:text-sm text-gray-500 hover:text-gray-800 underline underline-offset-2 order-2 sm:order-3">
            Back to collection
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-6 sm:py-10">
        {allProjects.length === 0 ? (
          <div className="text-center py-14 sm:py-20">
            <ShoppingBag size={48} className="mx-auto text-gray-200 mb-4"/>
            <h2 className="og-display text-xl sm:text-2xl italic mb-2">No saved photobooks yet</h2>
            <p className="text-gray-400 text-sm mb-6">Design a photobook and save it to add to your cart.</p>
            <Link to="/photobook"
              className="inline-block bg-[#1A1A1A] text-white rounded-full px-6 py-2.5 text-sm font-semibold hover:opacity-90">
              Browse Photobooks
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-5 sm:gap-6">
            {currentProjects.map((project) => (
              <div key={project.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="h-2" style={{ backgroundColor: project.bg || "#FFFFFF" }}/>
                <div className="p-4 sm:p-6 flex flex-col sm:flex-row gap-4 sm:gap-6 items-start">
                  {/* Spread thumbnails — mobile: horizontal scroll row; desktop: vertical stack */}
                  <div className="flex sm:flex-col gap-2.5 sm:gap-3 flex-shrink-0 overflow-x-auto sm:overflow-visible w-full sm:w-auto pb-1 sm:pb-0">
                    {(project.spreads || []).slice(0, 3).map((spread, si) => (
                      <SpreadThumb key={si} spread={spread} bg={project.bg}/>
                    ))}
                    {(project.spreads?.length || 0) > 3 && (
                      <p className="text-xs text-gray-400 text-center flex-shrink-0 self-center sm:self-auto whitespace-nowrap">+{project.spreads.length - 3} more</p>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex items-start justify-between gap-3 sm:gap-4 flex-wrap">
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Custom Photobook</p>
                        <h2 className="og-display text-xl sm:text-2xl leading-tight">{project.productName}</h2>
                        <p className="text-sm text-gray-500 mt-1">{project.projectName}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          {project.spreads?.length || 0} spreads •{" "}
                          {(project.spreads || []).reduce((t, s) =>
                            t + s.reduce((st, p) => st + (p.images || []).filter(Boolean).length, 0), 0
                          )} photos added
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xl sm:text-2xl font-bold">Rs. 1,499</p>
                        <p className="text-sm text-gray-400 line-through">Rs. 1,699</p>
                      </div>
                    </div>
                    <div className="flex items-center flex-wrap gap-2.5 sm:gap-3 mt-5 sm:mt-6">
                      <button
                        onClick={() => navigate(`/photobook/${project.slug}/editor`, {
                          state: { productName: project.productName, productBg: project.bg }
                        })}
                        className="px-4 sm:px-5 py-2.5 border border-gray-300 rounded-lg text-[13px] sm:text-sm font-semibold hover:bg-gray-50 transition-colors">
                        Edit Project
                      </button>
                      <button onClick={() => addProjectToCart(project)}
                        className="flex items-center gap-2 px-4 sm:px-5 py-2.5 bg-[#1A1A1A] text-white rounded-lg text-[13px] sm:text-sm font-semibold hover:opacity-90 transition-opacity">
                        <ShoppingBag size={15}/>
                        Add to Cart
                        <ChevronRight size={15}/>
                      </button>
                      <button onClick={() => deleteProject(project.id)}
                        className="ml-auto p-2 rounded-lg hover:bg-red-50 transition-colors">
                        <Trash2 size={16} className="text-gray-400 hover:text-red-400"/>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Proceed to checkout */}
            <div className="flex justify-center sm:justify-end">
              <Link to="/checkout"
                className="flex w-full sm:w-auto items-center justify-center gap-2 bg-[#1A1A1A] text-white rounded-xl px-8 py-3.5 sm:py-4 text-sm font-bold hover:opacity-90">
                Proceed to Checkout <ChevronRight size={16}/>
              </Link>
            </div>

            {/* Pagination — yellow style */}
            {totalPages > 1 && (
              <div className="flex flex-wrap justify-center items-center gap-1.5 sm:gap-2 mt-4 pb-4">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                  className="grid h-8 w-8 sm:h-9 sm:w-9 place-items-center rounded-full border border-gray-200 disabled:opacity-30 hover:bg-[#FFEB3B] transition-colors">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M15 18l-6-6 6-6"/></svg>
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button key={i} onClick={() => setCurrentPage(i + 1)}
                    className={`h-8 w-8 sm:h-9 sm:w-9 rounded-full text-sm font-bold transition-colors ${
                      currentPage === i + 1
                        ? "bg-[#FFEB3B] text-black"
                        : "hover:bg-gray-100 border border-transparent hover:border-gray-200"
                    }`}>
                    {i + 1}
                  </button>
                ))}

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="grid h-8 w-8 sm:h-9 sm:w-9 place-items-center rounded-full border border-gray-200 disabled:opacity-30 hover:bg-[#FFEB3B] transition-colors">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}